/**
 * EditorToolbar — Top bar with sequence browser, add step, play, import/export, save, close.
 */
import Phaser from 'phaser';
import { CANVAS_W, TOOLBAR_H, EditorEvents } from './EditorTypes';
import type { EditorState } from './EditorTypes';
import type { EditorHistory } from './EditorHistory';
import { TECH, fillTechRect, drawTechRect, createSmallTechButton, showTextInputDialog, showSelectionPopup, showConfirmDialog } from '@/ui/TechUI';
import { EventBus } from '@/events/EventBus';
import { STEP_TYPE_METAS, createDefaultStep } from './EditorStepFactory';
import { registerSequence, getSequence, getSequenceIds, deleteSequence } from '../CinematicDirector';
import type { CinematicStep, CinematicSequence, SequenceTrigger } from '../CinematicTypes';
import { SCENE_TRIGGER_EVENTS } from '../CinematicTypes';
import { collectCustomTranslations } from './EditorStepPanel';
import { addTranslations } from '@/i18n/I18n';

export class EditorToolbar {
  private scene: Phaser.Scene;
  private state: EditorState;
  private container: Phaser.GameObjects.Container;
  private idText!: Phaser.GameObjects.Text;
  private lockCamTxt!: Phaser.GameObjects.Text;
  private lockInTxt!: Phaser.GameObjects.Text;
  private targetSceneTxt!: Phaser.GameObjects.Text;
  private playBtn!: Phaser.GameObjects.Container;
  private undoRedoTxt!: Phaser.GameObjects.Text;
  private triggerContainer!: Phaser.GameObjects.Container;
  private history: EditorHistory | null = null;

  constructor(scene: Phaser.Scene, state: EditorState) {
    this.scene = scene;
    this.state = state;
    this.container = scene.add.container(0, 0).setDepth(10);
    this.build();
  }

  /** Inject history reference after construction (avoids circular dep) */
  setHistory(history: EditorHistory): void {
    this.history = history;
    this.updateUndoRedoDisplay();
  }

  private build(): void {
    // ── Background bar ──
    const gfx = this.scene.add.graphics();
    fillTechRect(gfx, 0, 0, CANVAS_W, TOOLBAR_H, 0x111122, 0.95);
    drawTechRect(gfx, 0, 0, CANVAS_W, TOOLBAR_H, TECH.DIM, 0.5, 1);
    this.container.add(gfx);

    const btnY = TOOLBAR_H / 2;

    // ── Row 1: Title + Sequence nav (left) — Action buttons (right) ──

    // Title
    const title = this.scene.add.text(8, 4, 'CINEMATIC', {
      fontSize: '10px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    });
    this.container.add(title);

    // ── Sequence navigator: [◀] id [▶] [☰] ──
    const navStartX = 8;
    const navY = 30;

    // Prev/Next as simple text buttons (small footprint)
    const prevBtn = this.scene.add.text(navStartX, navY, '◀', {
      fontSize: '12px', color: '#666688', fontFamily: 'monospace',
    }).setInteractive({ useHandCursor: true });
    prevBtn.on('pointerdown', () => this.navigateSequence(-1));
    this.container.add(prevBtn);

    this.idText = this.scene.add.text(navStartX + 18, navY, this.state.sequence.id, {
      fontSize: '11px', color: '#aaaacc', fontFamily: 'monospace',
    }).setInteractive({ useHandCursor: true });
    this.idText.on('pointerdown', () => {
      showTextInputDialog(this.scene, 'Sequence ID', this.state.sequence.id, (val) => {
        this.state.sequence.id = val;
        this.updateIdDisplay();
      });
    });
    this.container.add(this.idText);

    const nextBtn = this.scene.add.text(navStartX + 140, navY, '▶', {
      fontSize: '12px', color: '#666688', fontFamily: 'monospace',
    }).setInteractive({ useHandCursor: true });
    nextBtn.on('pointerdown', () => this.navigateSequence(1));
    this.container.add(nextBtn);

    const browseBtn = this.scene.add.text(navStartX + 160, navY, '☰', {
      fontSize: '12px', color: '#44aaff', fontFamily: 'monospace',
    }).setInteractive({ useHandCursor: true });
    browseBtn.on('pointerdown', () => this.openSequenceBrowser());
    this.container.add(browseBtn);

    // ── Lock toggles (compact, under title) ──
    this.lockCamTxt = this.scene.add.text(navStartX + 90, 4, '', {
      fontSize: '9px', color: '#888888', fontFamily: 'monospace',
    }).setInteractive({ useHandCursor: true });
    this.lockCamTxt.on('pointerdown', () => {
      this.state.sequence.lockCamera = !this.state.sequence.lockCamera;
      this.updateLockDisplay();
    });
    this.container.add(this.lockCamTxt);

    this.lockInTxt = this.scene.add.text(navStartX + 155, 4, '', {
      fontSize: '9px', color: '#888888', fontFamily: 'monospace',
    }).setInteractive({ useHandCursor: true });
    this.lockInTxt.on('pointerdown', () => {
      this.state.sequence.lockInput = !this.state.sequence.lockInput;
      this.updateLockDisplay();
    });
    this.container.add(this.lockInTxt);
    this.updateLockDisplay();

    // ── Target scene (clickable) ──
    this.targetSceneTxt = this.scene.add.text(navStartX + 185, navY, '', {
      fontSize: '9px', color: '#4466aa', fontFamily: 'monospace',
    }).setInteractive({ useHandCursor: true });
    this.targetSceneTxt.on('pointerdown', () => {
      const scenes = ['GameScene', 'LevelTreeScene', 'WorkshopScene', 'TitleScene', 'UIScene'];
      const items = scenes.map(s => ({ label: s, value: s, description: s }));
      showSelectionPopup(this.scene, 'Target Scene', items, (val) => {
        this.state.sequence.targetScene = val;
        this.updateTargetSceneDisplay();
      });
    });
    this.container.add(this.targetSceneTxt);
    this.updateTargetSceneDisplay();

    // ── Trigger tags row (below nav row) ──
    this.triggerContainer = this.scene.add.container(navStartX, navY + 16).setDepth(10);
    this.container.add(this.triggerContainer);
    this.rebuildTriggerTags();

    // ── Action buttons (right-aligned, evenly spaced) ──
    // Buttons: ADD, PLAY/STOP, STEP, IMPORT, EXPORT, SAVE
    const buttons = [
      { label: '+ADD',     color: TECH.ACCENT,  action: () => this.onAddStep() },
      { label: '__PLAY__', color: 0,            action: () => {} }, // placeholder — replaced by dynamic play/stop
      { label: '▶▶STEP',  color: 0x4488aa,     action: () => this.onSingleStep() },
      { label: 'IMPORT',   color: TECH.COLOR,   action: () => this.onImport() },
      { label: 'EXPORT',   color: TECH.COLOR,   action: () => this.onExport() },
      { label: 'SAVE',     color: TECH.SUCCESS, action: () => this.onSave() },
    ];

    const rightEdge = CANVAS_W - 100; // leave room for CLOSE + help
    const leftEdge = 200;
    const totalGap = rightEdge - leftEdge;
    const btnGap = totalGap / buttons.length;

    for (let i = 0; i < buttons.length; i++) {
      const b = buttons[i];
      const x = leftEdge + btnGap * i + btnGap / 2;
      if (b.label === '__PLAY__') {
        // Dynamic play/stop button
        this.playBtn = createSmallTechButton(this.scene, x, btnY, '▶PLAY', TECH.SUCCESS, () => this.onPlayStop(), 11);
        this.container.add(this.playBtn);
      } else {
        this.container.add(createSmallTechButton(this.scene, x, btnY, b.label, b.color, b.action, 11));
      }
    }

    // ── Undo/Redo counter (next to SAVE) ──
    this.undoRedoTxt = this.scene.add.text(rightEdge - 10, 4, '', {
      fontSize: '9px', color: '#666688', fontFamily: 'monospace',
    });
    this.container.add(this.undoRedoTxt);

    // Listen for preview state changes to toggle play/stop
    EventBus.on(EditorEvents.PREVIEW_START, this.onPreviewStarted, this);
    EventBus.on(EditorEvents.PREVIEW_STOP, this.onPreviewStopped, this);
    EventBus.on(EditorEvents.HISTORY_CHANGED, this.updateUndoRedoDisplay, this);

    // ── [?] Help — far right, before CLOSE ──
    this.container.add(createSmallTechButton(
      this.scene, CANVAS_W - 80, btnY, '?', 0x4466aa,
      () => EventBus.emit(EditorEvents.HELP_TOGGLE),
      11,
    ));

    // ── [X Close] — far right ──
    this.container.add(createSmallTechButton(
      this.scene, CANVAS_W - 45, btnY, 'CLOSE', TECH.DANGER,
      () => (this.scene as any).confirmClose?.() ?? (this.scene as any).closeEditor?.(),
      11,
    ));
  }

  // ── Play / Stop toggle ──

  private onPlayStop(): void {
    if (this.state.mode === 'preview') {
      EventBus.emit(EditorEvents.PREVIEW_STOP);
    } else if (this.state.mode === 'edit') {
      // Only allow play from edit mode (prevents double-play from rapid clicks)
      EventBus.emit(EditorEvents.PREVIEW_START);
    }
  }

  private onPreviewStarted(): void { this.rebuildPlayButton(true); }
  private onPreviewStopped(): void { this.rebuildPlayButton(false); }

  private rebuildPlayButton(isPlaying: boolean): void {
    const x = this.playBtn.x;
    const y = this.playBtn.y;
    this.playBtn.destroy();
    this.playBtn = createSmallTechButton(
      this.scene, x, y,
      isPlaying ? '■STOP' : '▶PLAY',
      isPlaying ? TECH.DANGER : TECH.SUCCESS,
      () => this.onPlayStop(), 11,
    );
    this.container.add(this.playBtn);
  }

  // ── Single Step ──

  private onSingleStep(): void {
    if (this.state.mode === 'preview') {
      // Already playing — stop first
      EventBus.emit(EditorEvents.PREVIEW_STOP);
      return;
    }
    if (this.state.sequence.steps.length === 0) return;
    // Auto-select first step if none selected
    if (this.state.selectedStepIndex < 0) {
      this.state.selectedStepIndex = 0;
      EventBus.emit(EditorEvents.STEP_SELECTED);
    }
    EventBus.emit(EditorEvents.PREVIEW_SINGLE_STEP);
  }

  // ── Undo/Redo display ──

  private updateUndoRedoDisplay = (): void => {
    if (!this.history) { this.undoRedoTxt.setText(''); return; }
    const { undo, redo } = this.history.getCounts();
    const parts: string[] = [];
    if (undo > 0) parts.push(`↩${undo}`);
    if (redo > 0) parts.push(`↪${redo}`);
    this.undoRedoTxt.setText(parts.join(' '));
  };

  // ── Button actions ──

  private onAddStep(): void {
    const items = STEP_TYPE_METAS.map(m => ({ label: `${m.icon} ${m.label}`, value: m.type, description: m.label }));
    showSelectionPopup(this.scene, 'Add Step', items, (val) => {
      const step = createDefaultStep(val as CinematicStep['type']);
      const idx = this.state.selectedStepIndex + 1;
      this.state.sequence.steps.splice(idx >= 0 ? idx : this.state.sequence.steps.length, 0, step);
      this.state.selectedStepIndex = idx >= 0 ? idx : this.state.sequence.steps.length - 1;
      EventBus.emit(EditorEvents.STEP_ADDED);
    });
  }

  private onImport(): void {
    showTextInputDialog(this.scene, 'Paste Sequence JSON', '', (json) => {
      try {
        const parsed = JSON.parse(json);
        if (!parsed.id || !Array.isArray(parsed.steps)) throw new Error('Invalid format');
        // Register any custom translations bundled with the sequence
        if (parsed.customTranslations) {
          addTranslations('en', parsed.customTranslations);
          addTranslations('fr', parsed.customTranslations);
        }
        this.switchToSequence(parsed);
      } catch (e) {
        console.warn('[CinematicEditor] Invalid JSON:', e);
      }
    });
  }

  /** Populate sequence.customTranslations from dialogue steps before save/export. */
  private syncCustomTranslations(): void {
    const translations = collectCustomTranslations(this.state.sequence);
    if (Object.keys(translations).length > 0) {
      this.state.sequence.customTranslations = translations;
    } else {
      delete this.state.sequence.customTranslations;
    }
  }

  private onExport(): void {
    this.syncCustomTranslations();
    const json = JSON.stringify(this.state.sequence, null, 2);
    navigator.clipboard.writeText(json).then(() => {
      this.showToast('JSON copied to clipboard');
    });
  }

  private onSave(): void {
    this.syncCustomTranslations();
    registerSequence(JSON.parse(JSON.stringify(this.state.sequence)));
    // Reset dirty flag so close-check won't warn about already-saved changes
    (this.scene as any).markClean?.();
    this.showToast(`Saved "${this.state.sequence.id}"`);
  }

  // ── Sequence navigation ──

  /** Get sequence IDs filtered by current host scene (includes untagged "any" sequences) */
  private getFilteredIds(): string[] {
    const host = this.state.hostSceneKey;
    return getSequenceIds().filter(id => {
      const seq = getSequence(id);
      return !seq?.targetScene || seq.targetScene === host;
    });
  }

  /** Navigate to prev/next registered sequence (filtered by host scene) */
  private navigateSequence(dir: number): void {
    const ids = this.getFilteredIds();
    if (ids.length === 0) return;
    const currentIdx = ids.indexOf(this.state.sequence.id);
    let nextIdx = currentIdx + dir;
    if (nextIdx < 0) nextIdx = ids.length - 1;
    if (nextIdx >= ids.length) nextIdx = 0;
    const seq = getSequence(ids[nextIdx]);
    if (seq) this.switchToSequence(JSON.parse(JSON.stringify(seq)));
  }

  /** Open sequence browser popup with sequences filtered by host scene + New/Delete */
  private openSequenceBrowser(): void {
    const ids = this.getFilteredIds();
    const host = this.state.hostSceneKey;
    const items = [
      { label: '+ New Sequence', value: '__new__', description: `Create blank (${host})` },
      ...ids.map(id => {
        const seq = getSequence(id);
        const tag = seq?.targetScene ?? 'any';
        return {
          label: id === this.state.sequence.id ? `▸ ${id}` : `  ${id}`,
          value: id,
          description: `${seq?.steps.length ?? 0} steps · ${tag}`,
        };
      }),
    ];

    // Add delete option if current sequence exists in registry
    if (ids.includes(this.state.sequence.id)) {
      items.push({
        label: `✕ Delete "${this.state.sequence.id}"`,
        value: '__delete__',
        description: 'Remove from registry',
      });
    }

    showSelectionPopup(this.scene, 'Sequences', items, (val) => {
      if (val === '__new__') {
        showTextInputDialog(this.scene, 'New Sequence ID', 'my-sequence', (id) => {
          if (!id) return;
          const newSeq: CinematicSequence = {
            id, lockCamera: true, lockInput: true, targetScene: this.state.hostSceneKey, steps: [],
          };
          this.switchToSequence(newSeq);
        });
      } else if (val === '__delete__') {
        showConfirmDialog(this.scene, `Delete "${this.state.sequence.id}" from registry?`, () => {
          deleteSequence(this.state.sequence.id);
          this.showToast(`Deleted "${this.state.sequence.id}"`);
          // Switch to first remaining (filtered) or create blank
          const remaining = this.getFilteredIds();
          if (remaining.length > 0) {
            const seq = getSequence(remaining[0]);
            if (seq) this.switchToSequence(JSON.parse(JSON.stringify(seq)));
          } else {
            this.switchToSequence({ id: 'new-sequence', lockCamera: true, lockInput: true, steps: [] });
          }
        });
      } else {
        const seq = getSequence(val);
        if (seq) this.switchToSequence(JSON.parse(JSON.stringify(seq)));
      }
    });
  }

  /** Replace the current sequence and notify all components */
  private switchToSequence(seq: CinematicSequence): void {
    this.state.sequence = seq;
    this.state.selectedStepIndex = -1;
    this.updateIdDisplay();
    this.updateLockDisplay();
    this.updateTargetSceneDisplay();
    this.rebuildTriggerTags();
    EventBus.emit(EditorEvents.SEQUENCE_SWITCHED);
  }

  // ── Trigger management ──

  private rebuildTriggerTags(): void {
    this.triggerContainer.removeAll(true);
    const triggers = this.state.sequence.triggers ?? [];
    let x = 0;

    // Existing trigger tags
    for (let i = 0; i < triggers.length; i++) {
      const t = triggers[i];
      const once = t.once ?? true;
      let label = t.event;
      if (t.waveNumber != null) label += `:${t.waveNumber}`;
      if (t.tile) label += `@${t.tile.col},${t.tile.row}`;
      if (once) label += ' (1x)';

      // Tag background
      const tagTxt = this.scene.add.text(x, 0, label, {
        fontSize: '8px', color: '#aaddff', fontFamily: 'monospace',
        backgroundColor: '#223344', padding: { x: 4, y: 2 },
      });
      this.triggerContainer.add(tagTxt);

      // Remove [×] button
      const removeBtn = this.scene.add.text(x + tagTxt.width + 2, 0, '×', {
        fontSize: '8px', color: '#ff6666', fontFamily: 'monospace',
      }).setInteractive({ useHandCursor: true });
      const idx = i;
      removeBtn.on('pointerdown', () => {
        if (!this.state.sequence.triggers) return;
        this.state.sequence.triggers.splice(idx, 1);
        if (this.state.sequence.triggers.length === 0) delete this.state.sequence.triggers;
        this.rebuildTriggerTags();
      });
      this.triggerContainer.add(removeBtn);

      // Toggle once on click
      tagTxt.setInteractive({ useHandCursor: true });
      tagTxt.on('pointerdown', () => {
        if (!this.state.sequence.triggers) return;
        const trig = this.state.sequence.triggers[idx];
        trig.once = !(trig.once ?? true);
        this.rebuildTriggerTags();
      });

      x += tagTxt.width + removeBtn.width + 6;
    }

    // [+ Trigger] button
    const addBtn = this.scene.add.text(x, 0, '+ Trigger', {
      fontSize: '8px', color: '#44ff88', fontFamily: 'monospace',
      backgroundColor: '#113322', padding: { x: 4, y: 2 },
    }).setInteractive({ useHandCursor: true });
    addBtn.on('pointerdown', () => this.onAddTrigger());
    this.triggerContainer.add(addBtn);
  }

  private onAddTrigger(): void {
    const sceneKey = this.state.sequence.targetScene;
    if (!sceneKey) {
      this.showToast('Set Target Scene first');
      return;
    }
    const events = SCENE_TRIGGER_EVENTS[sceneKey];
    if (!events || events.length === 0) {
      this.showToast(`No events for ${sceneKey}`);
      return;
    }
    const items = events.map(e => ({ label: e.label, value: e.event, description: e.label }));
    showSelectionPopup(this.scene, 'Trigger Event', items, (val) => {
      const meta = events.find(e => e.event === val);
      if (!meta) return;

      const newTrigger: SequenceTrigger = { event: val, once: true };

      // If event needs waveNumber, prompt for it
      if (meta.hasWaveNumber) {
        showTextInputDialog(this.scene, 'Wave Number (empty = any)', '', (wn) => {
          const num = parseInt(wn, 10);
          if (!isNaN(num) && num > 0) newTrigger.waveNumber = num;
          this.addTrigger(newTrigger);
        });
      } else if (meta.hasTile) {
        showTextInputDialog(this.scene, 'Tile col,row (e.g. 5,3)', '', (coords) => {
          const parts = coords.split(',').map(s => parseInt(s.trim(), 10));
          if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
            newTrigger.tile = { col: parts[0], row: parts[1] };
          }
          this.addTrigger(newTrigger);
        });
      } else {
        this.addTrigger(newTrigger);
      }
    });
  }

  private addTrigger(trigger: SequenceTrigger): void {
    if (!this.state.sequence.triggers) this.state.sequence.triggers = [];
    this.state.sequence.triggers.push(trigger);
    this.rebuildTriggerTags();
  }

  // ── UI updates ──

  private updateIdDisplay(): void {
    this.idText.setText(this.state.sequence.id);
  }

  private updateLockDisplay(): void {
    const cam = this.state.sequence.lockCamera;
    this.lockCamTxt.setText(`${cam ? '■' : '□'} LockCam`);
    this.lockCamTxt.setColor(cam ? '#ffcc00' : '#555566');
    const inp = this.state.sequence.lockInput;
    this.lockInTxt.setText(`${inp ? '■' : '□'} LockIn`);
    this.lockInTxt.setColor(inp ? '#ffcc00' : '#555566');
  }

  private updateTargetSceneDisplay(): void {
    const ts = this.state.sequence.targetScene;
    this.targetSceneTxt.setText(`⬟ ${ts ?? 'any'}`);
    this.targetSceneTxt.setColor(ts ? '#44aaff' : '#555566');
  }

  /** Brief toast notification */
  private showToast(msg: string): void {
    const toast = this.scene.add.text(CANVAS_W / 2, TOOLBAR_H + 10, msg, {
      fontSize: '11px', color: '#44ff88', fontFamily: 'monospace', backgroundColor: '#111122',
      padding: { x: 8, y: 4 },
    }).setOrigin(0.5, 0).setDepth(20);
    this.scene.tweens.add({
      targets: toast, alpha: 0, y: TOOLBAR_H - 5,
      duration: 1500, delay: 800, ease: 'Power2',
      onComplete: () => toast.destroy(),
    });
  }

  setVisible(visible: boolean): void { this.container.setVisible(visible); }
  destroy(): void {
    EventBus.off(EditorEvents.PREVIEW_START, this.onPreviewStarted, this);
    EventBus.off(EditorEvents.PREVIEW_STOP, this.onPreviewStopped, this);
    EventBus.off(EditorEvents.HISTORY_CHANGED, this.updateUndoRedoDisplay, this);
    this.container.destroy();
  }
}
